/**
 * Whether this learner is still in their first run, and what the app owes them next. F-141.
 *
 * Leo, on the sixteen minutes a research pass takes: *"nobody is going to wait 15 minutes for
 * something to happen, they'll go to a different tab or a different app, **or we ask them to do
 * things in the meantime**… the onboarding should take what it takes… **when they're done we know
 * they're reachable**."*
 *
 * So the wait is not a loading screen. It is when the product does its onboarding, and the thing
 * onboarding has to come away with is **reachability** — a schedule, and somewhere to deliver on
 * it. The owner was asked whether configuring the schedule should be mandatory and chose *no plan
 * screen until configured*, which is why this module answers with a gate rather than with a hint:
 * `configure` is a state in which the plan screen is not reachable, deep link or no deep link.
 *
 * **Notifications are behind the same gate** — Leo, walking the flow: *"yes part of the gate but we
 * must make sure that we handle device specificity as part of onboarding."* So there are two closed
 * stages and not one, and what keeps the second from being a wall is that the device decides what
 * can honestly be asked for: `reach.ts` holds that, and on a device that cannot grant push at all
 * the step is finished by saying plainly that email will carry the questions.
 *
 * **In a DOM-free module for `plan/step.ts`'s reason, and with one of its own.** The decision is
 * three facts wide and every one of its answers is a redirect, so getting it wrong does not look
 * like a bad screen — it looks like the app bouncing somebody between two addresses, which is the
 * failure `Landing` already carries a comment about. `packages/web/test/onboarding-gate.test.ts`
 * asserts it from plain Node; `App.tsx` holds the wiring, which nothing here can see.
 *
 * ---
 *
 * **This is a first-run flow, once per learner.** A learner stating their second objective does not
 * see it, and **nobody who already has a reach window is ever gated** — which is every learner who
 * exists on the day this ships. Both fall out of the rules below rather than out of a version
 * check, and the second one is worth stating precisely because the rules changed under it.
 *
 * A configured learner is not gated **because they can never hold a mark.** The gate's own closed
 * stages are what write one, and the first of those is only reachable while `windowConfigured` is
 * false — so an account that arrived configured has nothing in this browser's storage under its
 * key, `firstRun` answers `open`, and no stage in this file applies to it. That is a stronger
 * guarantee than "the mark does not matter when a window exists", which is what the first version
 * of this said and is no longer true: after the window is saved the mark is exactly what decides
 * which step of the flow a learner is on.
 *
 * The mark is keyed by learner (`markKey`) because a browser is not a person: under one shared key,
 * somebody walking the gate on a family laptop left a mark the next account inherited, and got two
 * example briefings above a thread three weeks old.
 *
 * ---
 *
 * **There is no per-plan version of this, and there cannot be one** (F-151 step 8). A gated learner
 * has exactly one plan — the gate closes only once an objective exists, and it closes in the
 * minutes after the *first* one is stated — so `SetupFacts` names an objective's status in the
 * singular deliberately: it is *the* plan, not a plan. `/welcome` carries no `?o=`, because the
 * thing it acquires is the reach window, which is global.
 *
 * **The plan chrome and this flow never appear together, and since F-162 that is enforced rather
 * than inherited.** The old argument was that the tab bar was imported by three screens and this
 * was not one of them, plus a slot cap of one at every tier. Both halves have expired: the cap
 * went to three on 2026-09-19 (`ENTITLEMENTS`, tier 99, which is every account's default), and
 * the selector lives in the shell above every screen rather than inside three of them. What holds
 * it now is `showsPlanChrome` in `nav.ts` — a positive list of the addresses the chrome belongs
 * above (`SECTIONS`, plus `/new-plan` since F-163), which `/welcome` is not on — refusing again
 * whenever `gateIsClosed` answers true. `nav.test.ts` asserts both halves, and asserts the gate
 * closes on `/new-plan` too: stating a *second* plan is not something a learner mid-first-run has
 * any business reaching, and with no plan at all `/plan` is already the form as the whole screen.
 */

/** As much of the learner's setup as this decision needs. */
export interface SetupFacts {
  /** The current objective's status, or `none` when there is not one. */
  status: string;
  /** Whether a reach window has actually been saved. The defaults do not count. */
  windowConfigured: boolean;
  /** How far through the first run this browser has seen this learner get. See `OnboardingMark`. */
  mark?: OnboardingMark;
}

/**
 * How far through the first run this browser has seen this learner get.
 *
 * **Values rather than a boolean, because the absent case is the load-bearing one.** A learner who
 * has been here for a month carries nothing, and nothing has to mean *not in a first run* rather
 * than *has not finished one* — otherwise the samples would appear in the thread of everybody who
 * already knows what a briefing looks like, which is the opposite of what this is for.
 *
 * `configuring` is written when the gate closes in front of somebody, so it is only ever set for a
 * learner who genuinely arrived at an unconfigured product with an objective in flight. **Every
 * other value is written by the learner pressing on**, which is the whole of the fix for what Leo
 * hit walking the flow:
 *
 * > *"i clicked save on my configuration for time and number of messages and never got to
 * > notifications and it took me to the next screen, that page needs to auto save and we need to
 * > more explicitly tell the user to move on"*
 *
 * The step used to be derived from whether a window had been saved, so saving *was* moving on —
 * and once the panel saves by itself, that reading would carry somebody to the next screen on the
 * first nudge of a slider. So the API decides whether the gate is **closed** and the mark decides
 * **where in it the learner is**, and the two are read in that order by `firstRun`. A learner
 * cannot skip a step by not saving, and cannot be moved by saving.
 *
 * **`reaching` and `reachable` are new, and the marks already in the wild are the reason
 * `ONBOARDING_KEY` carries a version.** The first draft of this paragraph said a browser holding a
 * pre-existing `configuring` would land on the configure step, and called that *"a handful of
 * accounts in the minutes around a deploy … one screen seen twice"*. Both halves were wrong, and
 * the review of this change is what established it:
 *
 * - **The two-value first run is deployed.** The bundle live on 2026-09-19 carries
 *   `nabu.onboarding`, `configuring` and the pre-rework copy, so marks exist in real browsers
 *   right now — the owner's among them, since the complaints this change answers came from
 *   walking that build.
 * - **Nothing expires one.** In that build `configuring` becomes `done` only when the learner
 *   reaches the chat, so anybody who walked the gate and stopped holds it indefinitely. There is
 *   no "minutes around a deploy".
 * - **The cost is not a repeated screen.** Under the rules below, `configuring` plus a saved
 *   window answers `configure`, which closes the gate — so a learner with a perfectly good reach
 *   window is redirected off `/plan`, `/chat`, `/progress` and `/settings` and taught what a plan
 *   is. That is the exact thing this feature's prose promises four times cannot happen.
 *
 * The key is versioned instead, so every mark written before this change reads as *no mark* —
 * which is the state a configured account is supposed to be in, and the one the rules below answer
 * `open` for. See `ONBOARDING_KEY`.
 */
export type OnboardingMark =
  /** The gate closed in front of them. They are on the configure step. */
  | 'configuring'
  /** They said they were finished configuring. They are on the notifications step. */
  | 'reaching'
  /** They finished the notifications step. The gate is open and the worked examples are owed. */
  | 'reachable'
  /** They have seen the worked examples. Nothing more is owed, ever. */
  | 'done';

const MARKS: readonly string[] = ['configuring', 'reaching', 'reachable', 'done'];

/** The stored value, or nothing. Anything this does not recognise is nothing. */
export function asMark(value: unknown): OnboardingMark | undefined {
  return typeof value === 'string' && MARKS.includes(value) ? (value as OnboardingMark) : undefined;
}

/**
 * What the app owes this learner right now.
 *
 * `unknown` is not one of the answers a screen acts on. It is what absent facts produce, and it
 * exists so that a redirect is never rendered against a reading that has not arrived — sending
 * somebody to one screen and then moving them reads as the app deciding twice. `useSetupState` in
 * `App.tsx` carried that note until this feature replaced it; `FirstRunProvider` carries it now.
 */
export type FirstRun =
  /** The facts have not been read yet. Wait; do not redirect. */
  | 'unknown'
  /** Nothing owed. The app behaves exactly as it does for everybody else. */
  | 'open'
  /**
   * The gate is closed: there is an objective in flight and nowhere to deliver it.
   *
   * The plan screen is not reachable from here — the owner's decision, taken as a gate rather than
   * as a nag, and it is what makes *"when they're done we know they're reachable"* true rather than
   * hopeful.
   */
  | 'configure'
  /**
   * The window is set; now the device.
   *
   * Step 2b: how a question actually arrives on *this* machine. Still behind the gate, because the
   * thing the first run exists to come away with is **reachability**, and a saved window with
   * nothing to deliver to is half of it. What keeps that from being a trap is that the step can
   * always be finished — see `reach.ts`: on a device that cannot grant push, email is the floor and
   * the screen says so before the learner presses on.
   */
  | 'reach'
  /**
   * Reachable, and has not been shown what arrives.
   *
   * Step 3 of the flow: the chat, pre-filled with worked examples. **Offered and not enforced** —
   * everything is reachable again by the time this is the answer.
   */
  | 'tour';

/**
 * The stage this learner is at.
 *
 * Four rules, in this order, and the order is the whole of it:
 *
 * 1. **No objective, no flow.** The plan screen is where an objective is stated, so gating it
 *    behind configuration would gate the only screen that can open the gate. Step 1 of the design
 *    is *state the objective*; this is what makes step 1 reachable.
 * 2. **An objective and no window is the gate.** Nothing else matters here — not the mark, not how
 *    far research has got. A learner who deep-links to `/plan` mid-flow lands back on the welcome
 *    screen, because the alternative is the sequence F-140's notice exists to apologise for. It is
 *    first because it is the only rule derived from the API, so no browser can talk its way past it
 *    by holding a mark.
 * 3. **After that, the mark says where they are.** The window exists, so what is left is which step
 *    of the first run this browser watched them reach. Saving no longer advances anything: the
 *    panel autosaves, and a step derived from "has a window been saved" would move the screen out
 *    from under somebody who had nudged a slider.
 * 4. **Nothing is owed to somebody this browser never watched walk through the gate.** An absent
 *    mark is `open`, which is why a learner who already had a reach window sees none of this — and
 *    why, structurally, they never can: the only thing that writes `configuring` is the welcome
 *    screen while rule 2 is answering, and rule 2 cannot answer for somebody who is configured.
 */
export function firstRun(facts: SetupFacts | undefined): FirstRun {
  if (!facts) return 'unknown';
  if (facts.status === 'none') return 'open';
  if (!facts.windowConfigured) return 'configure';
  switch (facts.mark) {
    case 'configuring':
      return 'configure';
    case 'reaching':
      return 'reach';
    case 'reachable':
      return 'tour';
    default:
      return 'open';
  }
}

/**
 * Whether the rest of the app is out of reach at this stage.
 *
 * One function rather than a comparison written at each call site, because the call sites are a
 * route guard and a redirect target and the two have to agree: a guard that sends somebody to a
 * screen the guard would itself bounce is an infinite redirect, and that is the shape this answer
 * exists to make impossible to write by accident. Both closing stages are rendered by `/welcome`,
 * and `onboarding-gate.test.ts` asserts that rather than leaving it to be noticed.
 *
 * **`reach` closes it too, which is the change F-141 did not originally make.** Leo: *"yes part of
 * the gate."* The promise the whole flow exists to keep is *"when they're done we know they're
 * reachable"*, and a saved window with no delivery path is a schedule for messages that go nowhere.
 * What stops that from being a wall is in `reach.ts`: the step is finishable on every device,
 * including the ones that cannot grant push, because email is a floor rather than a failure.
 */
export function gateIsClosed(stage: FirstRun): boolean {
  return stage === 'configure' || stage === 'reach';
}

/**
 * The mark to write when the learner presses on from this stage, or nothing if there is nowhere.
 *
 * **Here rather than in the screen, because "move on" is now a decision and not a side effect.**
 * It used to be one: saving a window changed a fact the stage was derived from, so the screen
 * advanced itself and the learner was carried somewhere they had not asked to go. Autosave would
 * have made that fire on the first slider nudge. So the advance is an explicit step, it is written
 * down in one place, and it is asserted from plain Node — `unknown` and `open` answer `undefined`,
 * which is what makes "there is nothing after this" a value rather than a missing branch.
 *
 * **`tour` answers `undefined` too, and that is not an omission.** The gate is open by then and
 * what finishes the tour is the learner reading the two illustrations, which are rendered in the
 * chat and not here — `Chat.tsx` writes `done` itself, against the samples. Routing it through
 * this function would let the welcome screen mark the tour finished on the press that navigates to
 * the chat, which is *before* anybody has seen a sample; `Chat` decides whether to show them from
 * the stage, so the samples would then never appear. This function is about the two steps the gate
 * is closed for, and it ends where the gate does.
 */
export function nextMark(stage: FirstRun): OnboardingMark | undefined {
  switch (stage) {
    case 'configure':
      return 'reaching';
    case 'reach':
      return 'reachable';
    default:
      return undefined;
  }
}

/**
 * Where the browser remembers how far through the first run this learner got.
 *
 * **Versioned, and the suffix is load-bearing rather than hygiene.** `nabu.onboarding` is live and
 * holds two-value marks written by the deployed first run; under the rules above a `configuring`
 * left in one of those browsers now closes the gate over the whole app for somebody who already has
 * a reach window. Bumping the key makes every pre-existing mark read as *absent*, which is exactly
 * what a configured account is supposed to carry and is the case `firstRun` answers `open` for. A
 * learner genuinely mid-flow is re-marked by `Welcome`'s effect on its next render, so the cost of
 * the bump is that a small number of people see the first screen of the flow once more — which is
 * what the first draft of this change wrongly believed the *alternative* cost to be.
 *
 * **Per device, deliberately, and it is the weaker of the two halves — but it is no longer only
 * about illustrations, and that sentence used to be this comment's whole argument.** The gate's
 * *entry* is still derived from the API, so nothing in a browser can let somebody past an unsaved
 * reach window. What the mark now decides is which of the two closed stages a learner is in, and
 * two consequences follow that are worth stating rather than discovering:
 *
 * - **A wrong or inherited mark gates rather than illustrates.** That is what versioning the key
 *   closes, and what `markKey`'s per-learner keying closed before it.
 * - **A cleared mark drops somebody out of the middle of the flow.** At `reaching`, clearing site
 *   data — or simply reloading in a browser that blocks storage, where `writeMark` fails silently
 *   and only React state was carrying it — answers `open`: they leave with a saved window and
 *   without the notifications step, and *"when they're done we know they're reachable"* quietly
 *   stops holding for them.
 *
 * That second one is an accepted weakness rather than a fixed bug, and it is accepted because the
 * floor underneath it is real: every account proved an email address to sign in, and F-122 mails
 * the question when push cannot reach. Somebody who falls out of the flow is reachable by mail and
 * can turn notifications on from the configuration screen, which is the same place they would have
 * been sent anyway. Putting the mark on the learner's row would close it, at the cost of an
 * endpoint, a write and a migration — worth doing the day this flow acquires a step whose fallback
 * is *not* already a working transport.
 */
export const ONBOARDING_KEY = 'nabu.onboarding.v2';

/**
 * The key this learner's mark is stored under.
 *
 * **Per learner, because a browser is not a person.** A single key meant that learner A walking
 * through the gate on a shared laptop left a `configuring` mark that learner B then inherited: B
 * has a reach window and a live objective, `firstRun` answers `tour`, and somebody three weeks into
 * the product gets two example briefings above their real thread. That contradicts the one rule
 * this feature states everywhere — *a learner who already has a reach window never sees this flow*
 * — which held for the gate (derived from the API, per session) and leaked through the tour.
 *
 * Keyed rather than cleared on sign-out for two reasons: signing out is not the only way accounts
 * change hands, and a key that has to be cleared somewhere else is a key that will one day not be.
 *
 * An unknown learner gets a key nothing is ever written under, so they get no tour. That is the
 * right direction to be wrong in: the cost of a missing tour is that somebody reads their first
 * real briefing without a worked example, and the cost of a wrong one is showing examples to
 * somebody who has been using the product for a month.
 */
export function markKey(userId: string | undefined): string {
  return `${ONBOARDING_KEY}:${userId ?? 'unknown'}`;
}

/**
 * The mark this browser holds for this learner, or nothing.
 *
 * Wrapped, because a browser with site data blocked **throws** on the read rather than answering
 * null — the same hazard `initialLocale` in `i18n.ts` is wrapped for. A throw here would take the
 * whole gate down with it, and the gate is the one decision in this file that must never fail
 * closed against somebody who is already configured.
 */
export function readMark(userId: string | undefined): OnboardingMark | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return asMark(localStorage.getItem(markKey(userId)));
  } catch {
    // No storage. The learner is treated as having no mark, which means the gate still holds
    // (it is derived from the API) and the samples are simply never offered.
    return undefined;
  }
}

/** Remembers how far they got. A failure is a forgotten tour, never a broken screen. */
export function writeMark(mark: OnboardingMark, userId: string | undefined): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(markKey(userId), mark);
  } catch {
    // See `readMark`. Nothing downstream depends on this having worked.
  }
}
